import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Spinner } from "@heroui/react";
import { CustomModal, Button, FormError, showToast, TextInput, SelectDropdown } from "../shared";
import { PROJECT_ROLE_OPTIONS } from "../../constants/roles";
import { useNetworkFilterSearch } from "../../lib/react-query/partners.ts";
import { useInviteMember } from "../../lib/react-query/projects";
import {
  capitalize,
  extractBackendErrorMessage,
  getFullName,
  getPartnerLocationLabel,
} from "../../utils/utils";
import { validateEmail } from "../../utils/validations";

const EMPTY_CLIENT_DETAILS = {
  contact_name: "",
  company_name: "",
  phone: "",
  address: "",
  industry: "",
};

const InviteMemberModal = ({ isOpen, onClose, projectId }) => {
  const { mutateAsync: inviteMember, isLoading: inviting } = useInviteMember();
  const [state, setState] = useState({ loading: false, error: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [role, setRole] = useState("COLLABORATOR");
  const [title, setTitle] = useState("");
  const [clientDetails, setClientDetails] = useState(EMPTY_CLIENT_DETAILS);
  const searchDebounceRef = useRef();

  useEffect(() => {
    if (!isOpen) {
      setState({ loading: false, error: null });
      setSearchQuery("");
      setDebouncedSearch("");
      setSelectedRecipient(null);
      setRole("COLLABORATOR");
      setTitle("");
      setClientDetails(EMPTY_CLIENT_DETAILS);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const trimmed = (searchQuery || "").trim();
    if (trimmed.length >= 2) {
      searchDebounceRef.current = setTimeout(() => {
        setDebouncedSearch(trimmed);
      }, 250);
    } else {
      setDebouncedSearch("");
    }
  }, [isOpen, searchQuery]);

  const { data: networkResults = [], isLoading: networkLoading } = useNetworkFilterSearch(debouncedSearch, isOpen);

  const parseError = (err) => extractBackendErrorMessage(err, "Failed to send invitation");

  const selectedRecipientLabel = useMemo(() => {
    if (!selectedRecipient) return "";
    const name = capitalize(getFullName(selectedRecipient));
    return name || selectedRecipient?.id || "";
  }, [selectedRecipient]);

  const handleInvite = async () => {
    try {
      const email = searchQuery.trim();
      const isEmailInvite = !selectedRecipient && validateEmail(email);
      if (!selectedRecipient?.id && !isEmailInvite) {
        setState((p) => ({ ...p, error: "Please select a recipient." }));
        return;
      }
      if (isEmailInvite && role !== "EXTERNAL_STAKEHOLDER") {
        setState((p) => ({ ...p, error: "New email invitations are for clients." }));
        return;
      }
      if (!role) {
        setState((p) => ({ ...p, error: "Please select a role." }));
        return;
      }
      setState({ loading: true, error: null });
      const payload = {
        role,
        title: title.trim() || undefined,
      };
      if (isEmailInvite) {
        payload.email = email;
      } else {
        payload.user_id = selectedRecipient.id;
      }
      if (role === "EXTERNAL_STAKEHOLDER") {
        Object.entries(clientDetails).forEach(([key, value]) => {
          const trimmed = value.trim();
          if (trimmed) payload[key] = trimmed;
        });
      }
      await inviteMember({ projectId, payload });
      showToast("Invitation sent successfully", "success", { position: "top-center" });
      onClose?.();
    } catch (err) {
      const message = parseError(err);
      setState({ loading: false, error: message });
      return;
    }
    setState({ loading: false, error: null });
  };

  return (
    <CustomModal
      title={"Invite Member"}
      open={isOpen}
      close={onClose}
      primaryButtonText={"Send Invite"}
      primaryButtonClick={handleInvite}
      primaryButtonLoading={state.loading || inviting}
      secondaryButtonText={"Cancel"}
      secondaryButtonClick={onClose}
    >
      <div className="flex flex-col gap-3">
        <SelectDropdown
          options={PROJECT_ROLE_OPTIONS}
          label="Role"
          name="role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        />

        <div className="relative">
          <TextInput
            label="Recipient"
            type="text"
            name="recipient"
            value={selectedRecipient ? selectedRecipientLabel : searchQuery}
            onChange={(e) => {
              setSelectedRecipient(null);
              setSearchQuery(e.target.value);
            }}
            placeholder="Search connections or enter an email..."
            disabled={!!selectedRecipient}
          />
          {!selectedRecipient && searchQuery?.trim().length >= 2 && (
            <div className="mt-2 max-h-60 overflow-auto border rounded-lg bg-white shadow">
              {networkLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Spinner size="sm" />
                </div>
              ) : networkResults?.length > 0 ? (
                networkResults.map((item) => {
                  const name = capitalize(getFullName(item));
                  return (
                    <button
                      key={item?.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-surface flex items-center gap-3"
                      onClick={() => {
                        setSelectedRecipient(item);
                        setSearchQuery("");
                      }}
                    >
                      <Avatar src={item?.profile_image || undefined} className="!size-6" />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{name || `ID ${item?.id}`}</span>
                        <span className="text-xs text-mediumgrey">
                          {item?.profession || getPartnerLocationLabel(item) || "Connection"}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-mediumgrey">
                  {validateEmail(searchQuery.trim()) && role === "EXTERNAL_STAKEHOLDER"
                    ? `Invite new client at ${searchQuery.trim()}`
                    : "No matches found"}
                </div>
              )}
            </div>
          )}
          {selectedRecipient && (
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-darkgrey">{selectedRecipientLabel}</span>
              <Button className="rounded-lg" variant="outlined" color="secondary" size="md" onClick={() => setSelectedRecipient(null)}>Change</Button>
            </div>
          )}
        </div>

        <TextInput
          label="Title (optional)"
          type="text"
          name="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Project Manager"
        />

        {role === "EXTERNAL_STAKEHOLDER" && (
          <div className="flex flex-col gap-3 border-t border-surface-border pt-3">
            <p className="text-sm font-medium text-darkgrey">Client details (optional)</p>
            <TextInput
              label="Client contact name"
              type="text"
              name="contact_name"
              value={clientDetails.contact_name}
              onChange={(e) => setClientDetails((current) => ({ ...current, contact_name: e.target.value }))}
              placeholder="e.g. Jordan Lee"
            />
            <TextInput
              label="Client company"
              type="text"
              name="company_name"
              value={clientDetails.company_name}
              onChange={(e) => setClientDetails((current) => ({ ...current, company_name: e.target.value }))}
              placeholder="e.g. Example Builders"
            />
            <TextInput
              label="Client phone"
              type="tel"
              name="phone"
              value={clientDetails.phone}
              onChange={(e) => setClientDetails((current) => ({ ...current, phone: e.target.value }))}
              placeholder="Optional phone number"
            />
            <TextInput
              label="Client address"
              type="text"
              name="address"
              value={clientDetails.address}
              onChange={(e) => setClientDetails((current) => ({ ...current, address: e.target.value }))}
              placeholder="Optional address"
            />
            <TextInput
              label="Client industry"
              type="text"
              name="industry"
              value={clientDetails.industry}
              onChange={(e) => setClientDetails((current) => ({ ...current, industry: e.target.value }))}
              placeholder="Optional industry"
            />
          </div>
        )}

        <FormError message={state.error} />
        <p className="text-[12px] text-grey mt-2">Search your connections or enter an email to invite a new client.</p>
      </div>
    </CustomModal>
  );
};

export default InviteMemberModal;
